import React from "react";
import { trialCountdown } from "../trialCountdown";
import type { Trial } from "../types/subscriptions.types";

export interface TrialMeterProps {
  /** The trial as `GET /billing/subscription/` reported it, or `null` while
   *  the read is still out — see the rail, which is the only caller. */
  trial: Trial | null | undefined;
}

/**
 * How much of the free trial is left, at the foot of the rail, while the end of
 * it is still a long way off.
 *
 * The bar fills as the fortnight is spent: empty on the day the trial is taken,
 * full on the day it ends, so it reads as progress through the trial rather
 * than as a tank running dry. The number beside it is what actually matters —
 * a bar on its own is a mood, and "6 days left" is a fact.
 *
 * Quiet on purpose. For most of the fortnight the trial is not a thing anybody
 * needs to act on, and the rail's own colours are what a fact of the workspace
 * should be drawn in. The last three days are the part worth pressing about,
 * and `TrialBanner` takes over there — this stands down at
 * `countdown.urgent` so exactly one of the two is ever drawn.
 *
 * Nothing at all where there is no trial running either — a workspace that
 * never took one, one whose trial has ended, and every paying workspace, which
 * is the majority and must not be shown a meter for a trial it is not on.
 */
const TrialMeter: React.FC<TrialMeterProps> = ({ trial }) => {
  const countdown = trialCountdown(trial);
  if (!countdown || countdown.urgent) return null;

  return (
    <div className="mb-5 3xl:mb-6 rounded-[8px] border-[0.5px] border-[#A6EDF5] bg-[#EFFBFD] px-3 py-2.5 3xl:px-3.5 3xl:py-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] 3xl:text-[14px] font-semibold text-[#00424B]">
          Free trial
        </span>
        <span className="text-[10px] 3xl:text-[12px] text-ink-muted whitespace-nowrap">
          {countdown.label}
        </span>
      </div>
      {/* The days are the value, not a percentage, and `valuenow` counts the
          same way the bar fills — days spent out of the fortnight. What is
          left is the part worth hearing, so `aria-valuetext` says it in the
          words printed beside it rather than "8 of 14". */}
      <div
        role="progressbar"
        aria-label="Free trial"
        aria-valuemin={0}
        aria-valuemax={countdown.totalDays}
        aria-valuenow={countdown.daysUsed}
        aria-valuetext={countdown.label}
        className="mt-2 h-1.5 3xl:h-2 w-full rounded-full bg-[#C9F1F7] overflow-hidden"
      >
        {/* A floor of 4px so the fill is never nothing at all: on the day the
            trial is taken the width is 0% and the track would read as a bar
            that has not been drawn rather than as one at its start. The nub is
            where the fill begins, and it grows out of the same rounded end. */}
        <div
          className="h-full rounded-full bg-brand-dark transition-[width] duration-500"
          style={{ width: `${countdown.elapsed * 100}%`, minWidth: "4px" }}
        />
      </div>
    </div>
  );
};

export default TrialMeter;
